import { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import Home from "@/pages/Home";
import Services from "@/pages/Services";
import ServiceDetail from "@/pages/ServiceDetail";
import About from "@/pages/About";
import Story from "@/pages/Story";
import WhyAnxya from "@/pages/WhyAnxya";
import UniversalPlatform from "@/pages/UniversalPlatform";
import Contact from "@/pages/Contact";
import Demo from "@/pages/Demo";
import Careers from "@/pages/Careers";
import Insights from "@/pages/Insights";
import InsightDetail from "@/pages/InsightDetail";
import AudienceDetail from "@/pages/AudienceDetail";
import PitchDeck from "@/pages/PitchDeck";
import Partners from "@/pages/Partners";
import Domains from "@/pages/Domains";
import Admin from "@/pages/Admin";
import EkamApp from "@/pages/EkamApp";
import EkamAbout from "@/pages/EkamAbout";
import EkamContact from "@/pages/EkamContact";
import EkamLegal from "@/pages/EkamLegal";
import EkamLogin, { EkamAuthCallback } from "@/pages/EkamAuth";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    if (window.__lenis) {
      window.__lenis.scrollTo(0, { immediate: true });
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname]);
  return null;
};

const useLenis = () => {
  const ref = useRef(null);
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    window.__lenis = lenis;
    ref.current = lenis;
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);
};

function RoutedApp() {
  const location = useLocation();
  const isEkam = location.pathname === "/" || location.pathname.startsWith("/ekam");
  if (location.hash?.includes("session_id=")) return <EkamAuthCallback />;

  return (
    <>
      {!isEkam && <Navbar />}
      <main>
        <Routes>
          <Route path="/" element={<EkamApp />} />
          <Route path="/home" element={<Home />} />
          <Route path="/services" element={<Services />} />
          <Route path="/services/:slug" element={<ServiceDetail />} />
          <Route path="/products/:slug" element={<ServiceDetail />} />
          <Route path="/audiences/:slug" element={<AudienceDetail />} />
          <Route path="/pitch" element={<PitchDeck />} />
          <Route path="/partners" element={<Partners />} />
          <Route path="/domains" element={<Domains />} />
          <Route path="/about" element={<About />} />
          <Route path="/story" element={<Story />} />
          <Route path="/why-anxya" element={<WhyAnxya />} />
          <Route path="/universal-ai-os" element={<UniversalPlatform />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/demo" element={<Demo />} />
          <Route path="/careers" element={<Careers />} />
          <Route path="/insights" element={<Insights />} />
          <Route path="/insights/:slug" element={<InsightDetail />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/ekam/login" element={<EkamLogin />} />
          <Route path="/ekam/about" element={<EkamAbout />} />
          <Route path="/ekam/contact" element={<EkamContact />} />
          <Route path="/ekam/privacy" element={<EkamLegal page="privacy" />} />
          <Route path="/ekam/terms" element={<EkamLegal page="terms" />} />
          <Route path="/ekam/trust" element={<EkamLegal page="trust" />} />
          <Route path="/ekam/security" element={<EkamLegal page="security" />} />
          <Route path="/ekam/cookies" element={<EkamLegal page="cookies" />} />
          <Route path="/ekam/subprocessors" element={<EkamLegal page="subprocessors" />} />
          <Route path="/ekam/dpa-baa" element={<EkamLegal page="dpa-baa" />} />
          <Route path="/ekam/service-agreement" element={<EkamLegal page="service-agreement" />} />
          <Route path="/ekam" element={<EkamApp />} />
        </Routes>
      </main>
      {!isEkam && <Footer />}
    </>
  );
}

function App() {
  useLenis();
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <RoutedApp />
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
